"use client";
import { env } from "@/env";

export interface PasswordlessEmailOptions {
  redirectTo?: string;
  locale?: string;
  displayName?: string;
  defaultRole?: string;
  allowedRoles?: string[];
  metadata?: Record<string, unknown>;
}

export interface SignInEmailPasswordlessResult {
  isSuccess: boolean;
  isError: boolean;
  error: { message: string; status?: number; error?: string } | null;
}

/**
 * Custom signInEmailPasswordless function that supports custom headers (e.g., for Turnstile).
 * The Nhost SDK's useSignInEmailPasswordless hook doesn't support headers, so we need this
 * custom implementation to pass the x-cf-turnstile-response header.
 */
export async function signInEmailPasswordless(
  email: string,
  options?: PasswordlessEmailOptions,
  requestOptions?: { headers?: Record<string, string> }
): Promise<SignInEmailPasswordlessResult> {
  const authUrl = env.NEXT_PUBLIC_NHOST_AUTH_URL;

  if (!authUrl) {
    return {
      isSuccess: false,
      isError: true,
      error: { message: "Auth URL not configured", error: "config_error" },
    };
  }

  try {
    const response = await fetch(`${authUrl}/signin/passwordless/email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...requestOptions?.headers,
      },
      body: JSON.stringify({
        email,
        options,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        isSuccess: false,
        isError: true,
        error: {
          message: errorData.message || "Failed to send passwordless email",
          status: response.status,
          error: errorData.error,
        },
      };
    }

    return {
      isSuccess: true,
      isError: false,
      error: null,
    };
  } catch (error) {
    return {
      isSuccess: false,
      isError: true,
      error: {
        message: error instanceof Error ? error.message : "Network error",
        error: "network",
      },
    };
  }
}
